/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, InputNumber, Switch, Table, message, Alert, Tooltip } from 'antd';
import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { useAuth } from '@isocel/front-common';

import './GestionMagasins.less';
import { Store } from '../../queries/api/types';
import { fetchStores, updateStore } from '../../queries/api/store';
import {
    isStoreInventoryEnabled,
    useInventoryConfig,
    useUpdateInventoryConfig,
    useUpdateInventoryStoreConfig,
} from '../../queries/api/store-inventory';

const GestionMagasins: React.FC = () => {
    const { user } = useAuth();
    const organizationId = user?.organization?.id;

    const [stores, setStores] = useState<Store[]>([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pendingChanges, setPendingChanges] = useState<
        Record<
            string,
            {
                receptionAutoActiver?: boolean;
                receptionAutoDelai?: number | null;
                inventoryEnabled?: boolean;
                aliceCode?: string;
            }
        >
    >({});
    const [savingId, setSavingId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const { data: inventoryConfig, isLoading: inventoryConfigLoading } = useInventoryConfig(organizationId);
    const { mutate: updateInventoryConfig, isLoading: masterUpdating } = useUpdateInventoryConfig();
    const { mutateAsync: updateInventoryStoreConfig } = useUpdateInventoryStoreConfig();

    // The master switch is derived from per-store switches (including pending changes).
    const masterEnabled =
        stores.length > 0
            ? stores.some((s) => {
                  const pending = pendingChanges[s.id]?.inventoryEnabled;
                  return pending !== undefined ? pending : isStoreInventoryEnabled(inventoryConfig, s.id);
              })
            : inventoryConfig?.enabled !== false;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchStores();
            setStores(data);
        } catch (error) {
            console.error('Error fetching stores:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    const handleToggleMaster = (checked: boolean) => {
        if (!organizationId) return;
        // Toggling the master applies to every store at once.
        updateInventoryConfig(
            { organizationId, enabled: checked },
            {
                onSuccess: () => {
                    message.success(
                        checked
                            ? 'Inventaire activé pour tous les magasins.'
                            : 'Inventaire désactivé pour tous les magasins.'
                    );
                },
                onError: () => {
                    message.error("Erreur lors de la mise à jour de la configuration d'inventaire.");
                },
            }
        );
    };

    const handleToggle = (record: Store, checked: boolean) => {
        setPendingChanges((prev) => ({
            ...prev,
            [record.id]: { ...prev[record.id], receptionAutoActiver: checked },
        }));
    };

    const handleInventoryToggle = (record: Store, checked: boolean) => {
        setPendingChanges((prev) => ({
            ...prev,
            [record.id]: { ...prev[record.id], inventoryEnabled: checked },
        }));
    };

    const handleDelayChange = (record: Store, value: number | null) => {
        setPendingChanges((prev) => ({
            ...prev,
            [record.id]: { ...prev[record.id], receptionAutoDelai: value },
        }));
    };

    const handleAliceCodeChange = (record: Store, value: string) => {
        setPendingChanges((prev) => ({
            ...prev,
            [record.id]: { ...prev[record.id], aliceCode: value },
        }));
    };

    const handleValider = async (record: Store) => {
        const changes = pendingChanges[record.id];
        if (!changes || Object.keys(changes).length === 0) return;
        setSavingId(record.id);
        try {
            const payload: Record<string, any> = {};
            if (changes.receptionAutoActiver !== undefined) {
                payload.receptionAutoActiver = changes.receptionAutoActiver;
            }
            if (changes.receptionAutoDelai !== undefined) {
                payload.receptionAutoDelai = changes.receptionAutoDelai;
            }
            if (changes.aliceCode !== undefined) {
                payload.aliceCode = changes.aliceCode;
            }
            if (Object.keys(payload).length > 0) {
                await updateStore(record.id, payload);
            }
            if (changes.inventoryEnabled !== undefined && organizationId) {
                await updateInventoryStoreConfig({
                    organizationId,
                    storeId: record.id,
                    enabled: changes.inventoryEnabled,
                });
            }
            setStores((prev) =>
                prev.map((s) =>
                    s.id === record.id
                        ? {
                              ...s,
                              ...(changes.receptionAutoActiver !== undefined && {
                                  receptionAutoActiver: changes.receptionAutoActiver,
                              }),
                              ...(changes.receptionAutoDelai !== undefined && {
                                  receptionAutoDelai: changes.receptionAutoDelai,
                              }),
                              ...(changes.aliceCode !== undefined && {
                                  aliceCode: changes.aliceCode,
                              }),
                          }
                        : s
                )
            );
            setPendingChanges((prev) => {
                const next = { ...prev };
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [record.id]: _removed, ...rest } = next;
                return rest;
            });
            message.success('Mise à jour effectuée avec succès.');
        } catch (error) {
            message.error('Erreur lors de la mise à jour');
        } finally {
            setSavingId(null);
        }
    };

    const columns = [
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'EAN',
            dataIndex: 'ean',
            key: 'ean',
            render: (ean: string) => ean || '-',
        },
        {
            title: 'Code Alice',
            dataIndex: 'aliceCode',
            key: 'aliceCode',
            render: (_: string, record: Store) => {
                const pending = pendingChanges[record.id]?.aliceCode;
                const value = pending !== undefined ? pending : record.aliceCode ?? '';
                return (
                    <Input
                        value={value}
                        placeholder="Non renseigné"
                        onChange={(e) => handleAliceCodeChange(record, e.target.value)}
                        className="gestion-magasins__code-input"
                    />
                );
            },
        },
        {
            title: 'Réception auto activée',
            dataIndex: 'receptionAutoActiver',
            key: 'receptionAutoActiver',
            render: (_: boolean, record: Store) => (
                <Switch
                    checked={pendingChanges[record.id]?.receptionAutoActiver ?? record.receptionAutoActiver}
                    onChange={(checked) => handleToggle(record, checked)}
                />
            ),
        },
        {
            title: 'Réception auto délai (jours)',
            dataIndex: 'receptionAutoDelai',
            key: 'receptionAutoDelai',
            render: (_: number, record: Store) => {
                const pendingDelai = pendingChanges[record.id]?.receptionAutoDelai;
                const effectiveDelai = pendingDelai !== undefined ? pendingDelai : record.receptionAutoDelai;
                const isActive = pendingChanges[record.id]?.receptionAutoActiver ?? record.receptionAutoActiver;
                const showHint = isActive && (effectiveDelai === null || effectiveDelai === undefined);
                return (
                    <div>
                        <InputNumber
                            min={0}
                            value={effectiveDelai ?? undefined}
                            placeholder="Non configuré"
                            onChange={(val) => handleDelayChange(record, val)}
                            className="gestion-magasins__delay-input"
                        />
                        {showHint && (
                            <Alert
                                type="warning"
                                showIcon
                                message="Aucun délai configuré — le magasin sera ignoré"
                                className="gestion-magasins__delay-alert"
                            />
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Inventaire activé',
            key: 'inventoryEnabled',
            render: (_: any, record: Store) => {
                const pending = pendingChanges[record.id]?.inventoryEnabled;
                const checked = pending !== undefined ? pending : isStoreInventoryEnabled(inventoryConfig, record.id);
                return (
                    <Switch
                        checked={checked}
                        disabled={inventoryConfigLoading || masterUpdating}
                        onChange={(value) => handleInventoryToggle(record, value)}
                    />
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Store) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    loading={savingId === record.id}
                    disabled={!pendingChanges[record.id]}
                    onClick={() => {
                        handleValider(record);
                    }}
                >
                    Valider
                </Button>
            ),
        },
    ];

    return (
        <div className="gestion-magasins">
            <div className="gestion-magasins__header">
                <h1 className="gestion-magasins__title">Gestion des magasins</h1>
                <Tooltip title="Active ou désactive l'inventaire pour l'ensemble des magasins.">
                    <div className="gestion-magasins__inventory-section">
                        <span className="gestion-magasins__inventory-label">Inventaires pour tous les magasins</span>

                        <div className="gestion-magasins__inventory-switch-row">
                            <span>{masterEnabled ? 'Activé' : 'Désactivé'}</span>

                            <Switch
                                checked={masterEnabled}
                                loading={inventoryConfigLoading || masterUpdating}
                                onChange={handleToggleMaster}
                            />
                        </div>
                    </div>
                </Tooltip>
            </div>

            <div className="gestion-magasins__table-container">
                <Input
                    placeholder="Rechercher par nom ou code"
                    prefix={<SearchOutlined />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                    className="gestion-magasins__search-input"
                />
                <Table
                    size="small"
                    columns={columns}
                    dataSource={stores.filter(
                        (s) =>
                            s.name.toLowerCase().includes(search.toLowerCase()) ||
                            s.code.toLowerCase().includes(search.toLowerCase())
                    )}
                    loading={loading}
                    rowKey={(record) => record.id}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default GestionMagasins;
